import { StytchB2B, StytchB2BProvider } from '@stytch/react/b2b';
import { StytchEventType } from '@stytch/vanilla-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  DEFAULT_SESSION_DURATION_MINUTES,
  STYTCH_B2B_STRINGS,
  STYTCH_STYLES,
} from '@/constants';
import { AppMessageSeverities } from '@/types';
import { Alert } from '@/ui';
import { clearCache } from '@/utils';

import type { AxiosBaseQueryError } from '@/store/api';
import type { SignInMethodResponse } from '@/store/api/authApi';
import type { StytchEvent } from '@stytch/vanilla-js';
import type { FormEvent } from 'react';

import MarketingInfo from '@/components/Signin/MarketingInfo';
import { getStytchB2BUIClient } from '@/lib/stytchB2B';
import {
  SIGN_IN_METHOD_FALLBACK,
  useGetAuthConfigQuery,
  useGetAuthStatusQuery,
  useGetSignInMethodMutation,
  useLogoutUserMutation,
} from '@/store/api/authApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';
import { storePostAuthRedirect } from '@/utils/authRedirect';
import { captureMessage } from '@/utils/errorMonitoring';
import { rememberSigninEmail } from '@/utils/signinEmail';
import { identifyEmail, track } from '@/utils/tracking';

import './stytch.css';

const isAuthDebugEnabled = (): boolean => {
  if (!import.meta.env.DEV) {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery =
      params.get('debug_auth') === '1' || params.get('debugAuth') === '1';
    const fromStorage = window.localStorage.getItem('DEBUG_AUTH') === 'true';
    return fromQuery || fromStorage;
  } catch {
    return false;
  }
};

const authDebug = (event: string, data?: Record<string, unknown>) => {
  if (!isAuthDebugEnabled()) {
    return;
  }

  // eslint-disable-next-line no-console -- dev-only gated debug logging
  console.info(`[auth-debug] ${event}`, data ?? {});
};

const DIRECT_LOGIN_FOR_SINGLE_MEMBERSHIP: {
  ignoreInvites: boolean;
  ignoreJitProvisioning: boolean;
  status: boolean;
} = {
  ignoreInvites: false,
  ignoreJitProvisioning: false,
  status: true,
};

// Loose shape check only: the widget validates the address itself.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

type Classification = SignInMethodResponse & { email: string };

// The discovery callback sends the typed email back in router state (never the
// URL) when OAuth returned a different address.
const getHandedBackEmail = (state: unknown): string => {
  const email = (state as { email?: unknown } | null)?.email;
  return typeof email === 'string' ? email.trim() : '';
};

type OAuthProvider =
  | { one_tap: boolean; type: 'google' }
  | { providerParams: { login_hint: string }; type: 'google' | 'microsoft' }
  | { type: 'microsoft' };

type WidgetView = {
  // Remounts the widget per view so its internal screen state never leaks
  // between steps; StytchB2B does not re-mount on config changes alone.
  key: string;
  // "Use a password instead" applies to an OAuth-only view whose email may
  // still use a password.
  offerPasswordSwitch: boolean;
  products: ('oauth' | 'passwords')[];
  providers: OAuthProvider[];
};

// Which widget the sign-in page shows. Without a classification (oauth_only,
// or the email step not used) this is today's widget.
const getWidgetView = (
  oauthOnly: boolean,
  oneTapEnabled: boolean,
  classification: Classification | null,
  usePasswordInstead: boolean,
): WidgetView => {
  const allProviders: OAuthProvider[] = [
    { one_tap: oneTapEnabled, type: 'google' },
    { type: 'microsoft' },
  ];
  if (
    oauthOnly ||
    !classification ||
    (classification.method === 'any' && classification.allowPassword)
  ) {
    return {
      key: 'all',
      offerPasswordSwitch: false,
      products: oauthOnly ? ['oauth'] : ['oauth', 'passwords'],
      providers: allProviders,
    };
  }
  const { allowPassword, email, method } = classification;
  // allow_password is false only for OAuth-required companies; Rails would
  // reject a password sign-in after the attempt, so never offer one. A mailbox
  // that would otherwise get passwords (unknown or consumer Microsoft) gets
  // both OAuth providers instead. (any with allowPassword returned above.)
  if (method === 'any' || (method === 'password' && !allowPassword)) {
    return {
      key: 'oauth-required',
      offerPasswordSwitch: false,
      products: ['oauth'],
      providers: [
        { providerParams: { login_hint: email }, type: 'google' },
        { providerParams: { login_hint: email }, type: 'microsoft' },
      ],
    };
  }
  if (method === 'password' || (usePasswordInstead && allowPassword)) {
    return {
      key: 'passwords',
      offerPasswordSwitch: false,
      products: ['passwords'],
      providers: allProviders,
    };
  }
  return {
    key: method,
    offerPasswordSwitch: allowPassword,
    products: ['oauth'],
    providers: [{ providerParams: { login_hint: email }, type: method }],
  };
};

const EmailStepForm = ({
  canContinue,
  email,
  isClassifying,
  onEmailChange,
  onSubmit,
}: {
  canContinue: boolean;
  email: string;
  isClassifying: boolean;
  onEmailChange: (email: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) => (
  <form
    className="-mt-4 w-full px-8 pb-8"
    data-testid="signin-email-step"
    noValidate
    onSubmit={onSubmit}
  >
    <div className="mb-6 flex items-center gap-2 text-sm text-[#262626]">
      <span className="h-px flex-1 bg-current" />
      or
      <span className="h-px flex-1 bg-current" />
    </div>
    <label className="mb-4 block" htmlFor="signin-email">
      {/* The label sits in the input outline, like the widget's fields. */}
      <span className="relative z-10 -mb-2.5 ml-2.5 block w-fit bg-white px-1 text-xs leading-4 text-[#262626]">
        Email
      </span>
      <input
        autoComplete="email"
        className="h-11 w-full rounded border border-[#ADBCC5] px-3 text-base text-[#262626] outline-none [font-family:inherit] focus:border-[#637A6F]"
        id="signin-email"
        inputMode="email"
        name="email"
        placeholder="example@email.com"
        type="email"
        value={email}
        onChange={(event) => onEmailChange(event.target.value)}
      />
    </label>
    <button
      className="h-11 w-full rounded border-none bg-[#637A6F] text-base font-bold text-white [font-family:inherit] disabled:opacity-50"
      disabled={!canContinue}
      type="submit"
    >
      {isClassifying ? 'Checking…' : 'Continue'}
    </button>
  </form>
);

const STYTCH_FIRST_STEP_STYLES = {
  ...STYTCH_STYLES,
  container: { borderColor: 'transparent', width: '100%' },
};

const LINK_BUTTON_CLASS =
  'cursor-pointer border-none bg-transparent p-0 text-sm text-[#637A6F] underline';

const UsePasswordLink = ({ onUsePassword }: { onUsePassword: () => void }) => (
  <div className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-4 py-3">
    <button className={LINK_BUTTON_CLASS} type="button" onClick={onUsePassword}>
      Use a password instead
    </button>
  </div>
);

type DiscoveredOrganization = {
  membership?: { type?: string };
};

type DiscoveryAuthenticateEventData = {
  discovered_organizations?: DiscoveredOrganization[];
  email_address?: string;
  intermediate_session_token?: string;
};

type SessionEventData = {
  intermediate_session_token?: string;
  member?: { email_address?: string };
  member_authenticated?: boolean;
  session_jwt?: string;
  session_token?: string;
  user?: { emails?: { email?: string }[] };
};

// Mirrors the widget's own direct-login rule (vanilla-js 5.x
// shouldAllowDirectLoginToOrganization): exactly one active membership and no
// invite or JIT-eligible org. In that case the widget exchanges the IST, or
// routes a primary_required org to its own method screen, without a picker.
const willDirectLogin = (organizations: DiscoveredOrganization[]): boolean => {
  const { ignoreInvites, ignoreJitProvisioning, status } =
    DIRECT_LOGIN_FOR_SINGLE_MEMBERSHIP;
  if (!status) {
    return false;
  }
  const types = organizations.map((org) => org.membership?.type ?? '');
  const activeCount = types.filter((type) => type === 'active_member').length;
  const hasBlockingConditions = types.some(
    (type) =>
      ((type === 'pending_member' || type === 'invited_member') &&
        !ignoreInvites) ||
      ((type === 'eligible_to_join_by_email_domain' ||
        type === 'eligible_to_join_by_oauth_tenant') &&
        !ignoreJitProvisioning),
  );
  return activeCount === 1 && !hasBlockingConditions;
};

type Handoff = {
  email: string;
  token: string;
  tokenType: 'intermediate_session' | 'password_session';
};

// Events after which the widget holds a member session (or, when MFA is
// required, an org-scoped intermediate session).
const SESSION_EVENT_TYPES = new Set<string>([
  StytchEventType.B2BDiscoveryIntermediateSessionExchange,
  StytchEventType.B2BPasswordAuthenticate,
  StytchEventType.PasswordAuthenticate,
  StytchEventType.PasswordCreate,
]);

// Cross-org password sign-in only yields an intermediate session. When the
// widget will direct-login it exchanges that IST itself and emits
// B2BDiscoveryIntermediateSessionExchange, which carries the session.
// Otherwise it would show its own org picker; Rails admission decides the
// organization instead.
const getDiscoveryAuthenticateHandoff = (
  data: DiscoveryAuthenticateEventData,
): Handoff | null | 'wait' => {
  if (willDirectLogin(data.discovered_organizations ?? [])) {
    return 'wait';
  }
  const token = data.intermediate_session_token ?? '';
  return token
    ? {
        email: data.email_address ?? '',
        token,
        tokenType: 'intermediate_session',
      }
    : null;
};

// The widget authenticated the member itself and left a Stytch session (or,
// when MFA is required, an intermediate session). The discovery callback owns
// handing either one to Rails.
const getSessionEventHandoff = (data: SessionEventData): Handoff | null => {
  const email =
    data.member?.email_address ?? data.user?.emails?.[0]?.email ?? '';
  const intermediateSessionToken =
    data.member_authenticated !== true
      ? (data.intermediate_session_token ?? '')
      : '';
  if (intermediateSessionToken) {
    return {
      email,
      token: intermediateSessionToken,
      tokenType: 'intermediate_session',
    };
  }
  const sessionToken = data.session_token || data.session_jwt || '';
  return sessionToken
    ? { email, token: sessionToken, tokenType: 'password_session' }
    : null;
};

const SignIn = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const handedBackEmail = getHandedBackEmail(location.state);
  const hasClassifiedHandedBackEmail = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [oneTapEnabled, setOneTapEnabled] = useState(false);
  const [initialAuthStatusResolved, setInitialAuthStatusResolved] =
    useState(false);
  const [logoutUser] = useLogoutUserMutation();
  const {
    data: authConfig,
    isLoading: isAuthConfigLoading,
    isError: isAuthConfigError,
  } = useGetAuthConfigQuery();
  const { error: authStatusError, isFetching: isAuthStatusFetching } =
    useGetAuthStatusQuery(undefined, { refetchOnMountOrArgChange: true });
  const [getSignInMethod, { isLoading: isClassifying }] =
    useGetSignInMethodMutation();
  const [emailInput, setEmailInput] = useState('');
  const [classification, setClassification] = useState<Classification | null>(
    null,
  );
  const [usePasswordInstead, setUsePasswordInstead] = useState(false);
  const hasTrackedSigninView = useRef(false);
  const hasResolvedInitialAuthStatus = useRef(false);

  const [search] = useSearchParams();
  const message = search.get('error');
  const redirect = search.get('redirect');
  const branch = search.get('branch');
  const branchToken = search.get('token');
  const branchEmail = search.get('email');
  const oauthOnly = search.get('oauth_only') !== null;
  const authConfigReady = !isAuthConfigLoading && initialAuthStatusResolved;
  const b2bLoginEnabled =
    !isAuthConfigError && authConfig?.b2bLoginEnabled === true;
  const effectiveOauthOnly = oauthOnly;
  // Email first steers people to their mailbox's OAuth provider; oauth_only
  // already limits the page to OAuth.
  const emailFirst = b2bLoginEnabled && !oauthOnly;
  const trimmedEmail = emailInput.trim();
  const canContinue = EMAIL_PATTERN.test(trimmedEmail) && !isClassifying;
  const b2bDiscoveryRedirectUrl = useMemo(() => {
    const url = new URL('/auth/discovery-callback', window.location.origin);
    return url.toString();
  }, []);
  const stytchB2B = useMemo(
    () => (b2bLoginEnabled ? getStytchB2BUIClient() : null),
    [b2bLoginEnabled],
  );

  // Every successful in-widget authentication is handed to the discovery
  // callback exactly once; OAuth leaves through discoveryRedirectURL instead.
  const hasHandedOff = useRef(false);

  const handOff = (
    event: StytchEvent,
    stytchTokenType: Handoff['tokenType'],
    token: string,
    email: string,
  ) => {
    if (hasHandedOff.current) {
      return;
    }
    hasHandedOff.current = true;

    if (email) {
      identifyEmail(email);
    }

    track('auth_password_submit', {
      auth_action:
        event.type === StytchEventType.PasswordCreate ? 'signup' : 'login',
      redirect,
      stytch_token_type: event.type.toLowerCase(),
    });

    const encodedToken = encodeURIComponent(token);
    const encodedRedirect = redirect
      ? `&redirect=${encodeURIComponent(redirect)}`
      : '';
    // onEvent is only wired to the B2B widget, so the discovery callback is
    // the one handoff target.
    navigate(
      `/auth/discovery-callback?stytch_token_type=${stytchTokenType}&token=${encodedToken}${encodedRedirect}`,
      { replace: true },
    );
  };

  const onEvent = (event: StytchEvent) => {
    if (hasHandedOff.current) {
      return;
    }

    let handoff: Handoff | null | 'wait';
    if (event.type === StytchEventType.B2BPasswordDiscoveryAuthenticate) {
      handoff = getDiscoveryAuthenticateHandoff(
        event.data as DiscoveryAuthenticateEventData,
      );
    } else if (SESSION_EVENT_TYPES.has(event.type)) {
      handoff = getSessionEventHandoff(event.data as SessionEventData);
    } else {
      return;
    }

    if (handoff === 'wait') {
      return;
    }
    if (!handoff) {
      captureMessage('auth_password_event_missing_session', {
        extra: { stytch_event_type: event.type },
        level: 'error',
      });
      return;
    }
    handOff(event, handoff.tokenType, handoff.token, handoff.email);
  };

  // Remembers the email for the discovery callback's mismatch check (the
  // sign-in mount's clearCache wipes it on every fresh visit).
  const classifyEmail = async (email: string, preferPassword: boolean) => {
    rememberSigninEmail(email);
    let result: SignInMethodResponse;
    try {
      result = await getSignInMethod({ email }).unwrap();
    } catch {
      result = SIGN_IN_METHOD_FALLBACK;
    }
    track('auth_signin_method_classified', {
      allow_password: result.allowPassword,
      method: result.method,
    });
    setUsePasswordInstead(preferPassword);
    setClassification({ ...result, email });
  };

  const onSubmitEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canContinue) {
      return;
    }
    await classifyEmail(trimmedEmail, false);
  };

  const onChooseIndividual = () => {
    if (!branchToken) {
      return;
    }

    const encodedRedirect = redirect
      ? `&redirect=${encodeURIComponent(redirect)}`
      : '';
    navigate(
      `/auth/discovery-callback?stytch_token_type=discovery_confirm&token=${encodeURIComponent(branchToken)}${encodedRedirect}`,
      { replace: true },
    );
  };

  const onChooseCompany = () => {
    navigate('/support?reason=company-eligible', { replace: true });
  };

  const companyEligibleBranchContent =
    branch === 'company_eligible' && branchToken && branchEmail ? (
      <div className="w-full rounded-md bg-white p-6 shadow-md">
        <p className="mb-3 text-lg font-bold">Company or individual account?</p>
        <p className="mb-4 text-sm text-gray-700">
          This email may be eligible for access through a company workspace. You
          can continue with the company path, or create a separate self-paid
          individual account.
        </p>
        <p className="mb-6 text-sm text-gray-700">
          Account: <strong>{branchEmail}</strong>
        </p>
        <div className="flex w-full flex-col gap-3">
          <button
            className="rounded-md bg-[#637A6F] px-4 py-3 text-white"
            type="button"
            onClick={onChooseCompany}
          >
            Continue with company
          </button>
          <button
            className="rounded-md border border-[#637A6F] px-4 py-3 text-[#637A6F]"
            type="button"
            onClick={onChooseIndividual}
          >
            Create self-paid individual account
          </button>
        </div>
      </div>
    ) : null;

  const widgetView = getWidgetView(
    oauthOnly,
    oneTapEnabled,
    classification,
    usePasswordInstead,
  );
  const widgetOffersPasswords = widgetView.products.includes('passwords');

  // First screen: Google and Microsoft from the widget (OAuth only, so it
  // keeps its logo and title), then the email step that picks the sign-in
  // method for everyone else. One card, so the widget drops its own border.
  const emailStep = (
    <StytchB2BProvider stytch={stytchB2B!}>
      <div className="mento-stytch w-full rounded-lg border border-[#ADBCC5] bg-white">
        <StytchB2B
          key="first-step"
          callbacks={{
            onEvent,
          }}
          config={{
            authFlowType: 'Discovery',
            directLoginForSingleMembership: {
              ...DIRECT_LOGIN_FOR_SINGLE_MEMBERSHIP,
            },
            disableCreateOrganization: true,
            oauthOptions: {
              discoveryRedirectURL: b2bDiscoveryRedirectUrl,
              providers: getWidgetView(oauthOnly, oneTapEnabled, null, false)
                .providers,
            },
            products: ['oauth'],
            sessionOptions: {
              sessionDurationMinutes: DEFAULT_SESSION_DURATION_MINUTES,
            },
          }}
          styles={STYTCH_FIRST_STEP_STYLES}
        />
        <EmailStepForm
          canContinue={canContinue}
          email={emailInput}
          isClassifying={isClassifying}
          onEmailChange={setEmailInput}
          onSubmit={onSubmitEmail}
        />
      </div>
    </StytchB2BProvider>
  );

  let authContent = null;
  if (authConfigReady) {
    if (emailFirst && !classification) {
      authContent = emailStep;
    } else if (b2bLoginEnabled) {
      authContent = (
        <StytchB2BProvider stytch={stytchB2B!}>
          <div className="mento-stytch">
            <StytchB2B
              key={widgetView.key}
              callbacks={{
                onEvent,
              }}
              config={{
                authFlowType: 'Discovery',
                directLoginForSingleMembership: {
                  ...DIRECT_LOGIN_FOR_SINGLE_MEMBERSHIP,
                },
                disableCreateOrganization: true,
                oauthOptions: {
                  discoveryRedirectURL: b2bDiscoveryRedirectUrl,
                  providers: widgetView.providers,
                },
                passwordOptions: {
                  // Reset links (stytch_token_type=discovery for cross-org
                  // passwords) land on /reset, which completes them headlessly.
                  discoveryRedirectURL: `${window.location.origin}/reset`,
                  loginRedirectURL: b2bDiscoveryRedirectUrl,
                  resetPasswordRedirectURL: `${window.location.origin}/reset`,
                },
                products: widgetView.products,
                sessionOptions: {
                  sessionDurationMinutes: DEFAULT_SESSION_DURATION_MINUTES,
                },
              }}
              strings={STYTCH_B2B_STRINGS}
              styles={STYTCH_STYLES}
            />
          </div>
          {widgetView.offerPasswordSwitch && (
            <UsePasswordLink
              onUsePassword={() => setUsePasswordInstead(true)}
            />
          )}
          {widgetOffersPasswords && (
            <div
              className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-center text-xs leading-5 text-gray-600"
              data-testid="signin-password-note"
            >
              <p className="m-0">
                Outlook/Hotmail account? Use email and password
              </p>
              <p className="m-0">
                Past email link user? Choose &ldquo;Sign up or reset
                password&rdquo;
              </p>
            </div>
          )}
        </StytchB2BProvider>
      );
    } else {
      authContent = (
        <Alert className="w-full" severity="error">
          Sign in is temporarily unavailable.
        </Alert>
      );
    }
  }

  // "Sign in as <typed>" from the callback: classify that email as if it was
  // typed, and offer its password when the company allows one. Waits for the
  // mount's clearCache so the remembered email survives.
  useEffect(() => {
    if (
      !loaded ||
      !authConfigReady ||
      !emailFirst ||
      !handedBackEmail ||
      hasClassifiedHandedBackEmail.current
    ) {
      return;
    }
    hasClassifiedHandedBackEmail.current = true;
    setEmailInput(handedBackEmail);
    classifyEmail(handedBackEmail, true);
  }, [authConfigReady, emailFirst, handedBackEmail, loaded]);

  useEffect(() => {
    if (message) {
      dispatch(
        setAppMessage({
          message,
          open: true,
          severity: AppMessageSeverities.Error,
        }),
      );
      captureMessage('auth_signin_error', {
        extra: { message },
        level: 'error',
      });
    }
  }, [message]);

  useEffect(() => {
    if (hasTrackedSigninView.current || isAuthConfigLoading) {
      return;
    }

    hasTrackedSigninView.current = true;
    track('auth_signin_viewed', {
      oauth_only: effectiveOauthOnly,
      b2b_login_enabled: b2bLoginEnabled,
      redirect,
    });
  }, [isAuthConfigLoading, effectiveOauthOnly, b2bLoginEnabled, redirect]);

  useEffect(() => {
    if (hasResolvedInitialAuthStatus.current || isAuthStatusFetching) {
      return;
    }

    hasResolvedInitialAuthStatus.current = true;
    setOneTapEnabled(
      (authStatusError as AxiosBaseQueryError | undefined)?.status === 401,
    );
    setInitialAuthStatusResolved(true);

    (async () => {
      authDebug('signin_mount', { href: window.location.href });
      try {
        await logoutUser().unwrap();
        authDebug('signin_logout_success');
      } catch {
        // Ignore logout errors on sign in page
        authDebug('signin_logout_failed');
      }
      clearCache();
      storePostAuthRedirect(redirect);
      setLoaded(true);
    })();
  }, [authStatusError, isAuthStatusFetching, logoutUser, redirect]);

  return (
    <div className="h-screen w-screen bg-signin bg-cover bg-center bg-no-repeat">
      <div className="w-full flex-1 md:flex md:h-full">
        <MarketingInfo />
        <div className="flex w-full flex-1 items-center justify-center">
          {loaded && (
            <div className="flex w-[400px] flex-col items-center">
              {message && (
                <Alert className="mb-4 w-full" severity="error">
                  {message}
                </Alert>
              )}
              {oauthOnly && (
                <div className="w-full rounded-md bg-blue-100 p-4 text-center text-blue-800 shadow-md">
                  <p className="text-lg font-bold">Authentication Required</p>
                  <p>
                    Please use Google or Microsoft with your work account
                    instead of a password.
                  </p>
                </div>
              )}
              {companyEligibleBranchContent ?? authContent}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
